import React from "react";
import { CheckCircle, WarningAmber, Cancel } from "@mui/icons-material";
import type { InitiatorTrustPayload } from "./InitiatorTrustBanner";
import { criterionLevelOf, getInitiatorCriteria, REGISTRY_SIGNER_NOTE, type InitiatorTrustSettings } from "./initiatorTrustCriteria";

const GREEN = "#4CAF50";
const AMBER = "#F9A825";
const RED = "#E53935";

type Props = {
    trust: InitiatorTrustPayload;
    iconSize?: number;
    className?: string;
    /** Короткие подписи для плашки; в попапе — полные формулировки */
    compact?: boolean;
    /** Режим показа критерия селфи: hidden | trusted_only | all */
    selfieDisplayMode?: string;
    /** Владелец профиля — видит FAILED/PENDING селфи даже при trusted_only */
    viewerIsOwner?: boolean;
    trustSettings?: Partial<InitiatorTrustSettings> | null;
};

export default function InitiatorTrustChecklist({
    trust,
    iconSize = 18,
    className,
    compact = false,
    selfieDisplayMode,
    viewerIsOwner,
    trustSettings,
}: Props) {
    const items = getInitiatorCriteria(trust, { selfieDisplayMode, viewerIsOwner, trustSettings });
    if (items.length === 0) {
        return null;
    }

    const emptyInnFailed = items.some((row) => row.kind === "registry_empty" && !row.ok)
    const registryFailed = items.filter((row) => row.kind === "registry" && !row.ok)
    const visibleItems = compact && !viewerIsOwner && emptyInnFailed
        ? items.filter((row) => row.kind !== "registry_empty")
        : compact && !viewerIsOwner && registryFailed.length
        ? [
            {
                label: REGISTRY_SIGNER_NOTE,
                labelShort: REGISTRY_SIGNER_NOTE,
                ok: false,
                kind: "registry" as const,
            },
            ...items.filter((row) => row.kind !== "registry"),
        ]
        : items

    const criteriaClass =
        className ??
        (compact
            ? "initiator-trust-banner-v2__criteria initiator-trust-banner-v2__criteria--compact"
            : "initiator-trust-banner-v2__criteria");

    return (
        <div className={criteriaClass} role="list">
            {visibleItems.map((row, i) => {
                const level = criterionLevelOf(row);
                return (
                    <div key={i} className="initiator-trust-banner-v2__criterion" role="listitem">
                        {level === "green" ? (
                            <CheckCircle sx={{ fontSize: iconSize, color: GREEN, flexShrink: 0, marginTop: "1px" }} aria-hidden />
                        ) : level === "red" ? (
                            <Cancel sx={{ fontSize: iconSize, color: RED, flexShrink: 0, marginTop: "1px" }} aria-hidden />
                        ) : (
                            <WarningAmber sx={{ fontSize: iconSize, color: AMBER, flexShrink: 0, marginTop: "1px" }} aria-hidden />
                        )}
                        <span className="initiator-trust-banner-v2__criterion-label">
                            {compact ? row.labelShort : row.label}
                        </span>
                    </div>
                );
            })}
        </div>
    );
}
